import React, { useState } from "react";
import {
  Box,
  Paper,
  Typography,
  Link,
  CircularProgress,
  Stack,
  FormControlLabel,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import VisibilityIcon from "@mui/icons-material/Visibility";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";
import AppleIcon from "@mui/icons-material/Apple";
import IconButton from "@mui/material";

import withAuthentication from "../../hoc/withAuthentication";
import { useFormik } from "formik";
import { useNavigate, useLocation } from "react-router-dom";
import { enqueueSnackbar } from "notistack";
import { useUserLogin } from "../../services/api/user.api";
import { useGoogleSignIn } from "../../services/api/googleAuth.api";
import Footer from "../../components/Footer/Footer";
import { motion } from "framer-motion";
import CompanyPhoto from "../../components/assets/about/CompanyPhoto.png";
import {
  SignInMainContainer,
  GradientButton,
  GradientCheckbox,
  InputField,
  InputWrapper,
  InputIcon,
  Input,
  PasswordToggle,
  SocialButton,
  InputFieldLabel,
} from "./SignInDesign";
import { Icons } from "../../components/assets/icon";
import { loginValidationSchema } from "./SignIn.util";
import { messaging } from "../../utils/firebase";
import { getToken } from "firebase/messaging";
import { REACT_APP_NOTIFICATION_VAPID_KEY } from "../../constants/config";
import { useAuth } from "../../hooks";
import { StageLinkLogo } from "../../components/Branding/StageLinkLogo";

type SignInFormProps = {
  text?: string;
};

const SignInForm: React.FC<SignInFormProps> = ({ text }) => {
  const navigate = useNavigate();
  const location = useLocation();

  const redirectPath = location.state?.redirect;
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const isSmall = useMediaQuery(theme.breakpoints.down("sm"));

  const [showPassword, setShowPassword] = useState(false);
  const { update, isLoading: loading } = useUserLogin({ key: ["user_login"] });
  const { handleGoogleSignIn, isLoading: googleLoading } = useGoogleSignIn();

  const { userInfo } = useAuth();
  const isGuest = userInfo ? false : true;

  // ------------------ formik ------------------
  const { errors, touched, handleSubmit, handleChange, values, handleBlur } =
    useFormik({
      initialValues: { email: "", password: "" },
      validationSchema: loginValidationSchema,
      validateOnBlur: false,
      onSubmit: async (formValues) => {
        let token: string | null = null;

        try {
          if (Notification.permission !== "denied") {
            const permission = await Notification.requestPermission();
            if (permission === "granted") {
              token = await getToken(messaging, {
                vapidKey: REACT_APP_NOTIFICATION_VAPID_KEY,
              });
            }
          }
        } catch (err) {
          console.error("FCM token error:", err);
        }

        try {
          await update(
            { ...formValues, fcm_token: token, device_type: "web" },
            {
              onSuccess: (data) => {
                localStorage.setItem(
                  "access_token",
                  data.data?.access_token || ""
                );
                enqueueSnackbar("User login successful", {
                  variant: "success",
                });
                data?.data?.user?.account_type === "Business"
                  ? navigate(`/business/${data.data?.user?.id}`)
                  : navigate(redirectPath ?? `/user/${data.data?.user?.id}`);
                localStorage.setItem("isGuestLite", "false");
              },
              onError: (apiErr: any) =>
                enqueueSnackbar(
                  apiErr?.response?.data?.message || "Login failed",
                  { variant: "error" }
                ),
            }
          );
        } catch (err: any) {
          enqueueSnackbar(err?.response?.data?.message || "Login failed", {
            variant: "error",
          });
        }
      },
    });

  return (
    <Paper
      elevation={0}
      sx={{
        position: "relative",
        p: { xs: 3, sm: 5, md: 6 },
        borderRadius: 3,
        overflow: "hidden",
        bgcolor: "#141414",
        color: "#fff",
        border: "1px solid rgba(255,255,255,0.12)",
        boxShadow: "0 16px 48px rgba(0,0,0,0.5)",
        "::before": {
          content: '""',
          position: "absolute",
          inset: 0,
          pointerEvents: "none",
          background:
            "radial-gradient(900px 420px at 20% -10%, rgba(229,9,20,0.16), transparent 60%)",
        },
      }}
    >
      {/* Heading */}
          <StageLinkLogo/>


      {!isGuest && (
        <Typography
          fontFamily="Lexend"
          variant={isMobile ? "h6" : "h5"}
          mb={3}
          sx={{ color: "rgba(255,255,255,0.75)", fontWeight: 700 }}
        >
          Hi, Welcome!
        </Typography>
      )}

      {isGuest && (
        <Typography mb={3} sx={{ font: "var(--semibold-md)", color: "rgba(255,255,255,0.85)" }}>
          {text}
        </Typography>
      )}

      {/* ------------ FORM ------------ */}
      <form
        onSubmit={handleSubmit}
        style={{
          display: "flex",
          flexDirection: "column",
          width: "100%",
        }}
      >
        {/* Email */}
        <InputField sx={{ mb: 2 }}>
          <InputFieldLabel>Email address</InputFieldLabel>
          <InputWrapper>
            <Input
              name="email"
              type="email"
              placeholder="Email address"
              onChange={handleChange}
              onBlur={handleBlur}
              value={values.email}
            />
            <InputIcon>
              <Icons.EmailAddress />
            </InputIcon>
          </InputWrapper>
          {touched.email && errors.email && (
            <Typography variant="caption" color="#ff6b6b">
              {errors.email}
            </Typography>
          )}
        </InputField>

        {/* Password */}
        <InputField>
          <InputFieldLabel>Password</InputFieldLabel>
          <InputWrapper>
            <Input
              name="password"
              type={showPassword ? "text" : "password"}
              placeholder="Password"
              onChange={handleChange}
              onBlur={handleBlur}
              value={values.password}
              sx={{ pr: 5 }}
            />
            <InputIcon>
              <Icons.PasswordLock />
            </InputIcon>
            <PasswordToggle onClick={() => setShowPassword(!showPassword)}>
              {showPassword ? (
                <VisibilityOffIcon sx={{ color: "rgba(255,255,255,0.75)" }} />
              ) : (
                <VisibilityIcon sx={{ color: "rgba(255,255,255,0.75)" }} />
              )}
            </PasswordToggle>
          </InputWrapper>
          {touched.password && errors.password && (
            <Typography variant="caption" color="#ff6b6b">
              {errors.password}
            </Typography>
          )}
        </InputField>

        {/* Remember / Forgot */}
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            my: 1.5,
          }}
        >
          <FormControlLabel
            control={<GradientCheckbox />}
            label="Remember me"
            sx={{
              fontFamily: "Lexend",
              color: "rgba(255,255,255,0.85)",
              "& .MuiFormControlLabel-label": {
                fontWeight: 600,
              },
            }}
          />
          <Link
            href="/forgot-password"
            underline="hover"
            sx={{
              fontFamily: "Lexend",
              color: "#E50914",
              fontWeight: 700,
              "&:hover": { color: "#ff0f1a" },
            }}
          >
            Forgot password?
          </Link>
        </Box>

        {/* Submit */}
        <GradientButton
          type="submit"
          disabled={loading || !values.email || !values.password}
        >
          {loading ? (
            <CircularProgress size={24} sx={{ color: "#fff" }} />
          ) : (
            "Log In"
          )}
        </GradientButton>
      </form>

      {/* Social login */}
      <Typography
        variant="body2"
        mt={4}
        align="center"
        fontFamily="Lexend"
        sx={{ color: "rgba(255,255,255,0.65)" }}
      >
        Or continue with
      </Typography>

      <Stack direction="row" spacing={2} justifyContent="center" mt={2}>
        <SocialButton
          onClick={handleGoogleSignIn}
          disabled={googleLoading || loading}
        >
          {googleLoading ? (
            <CircularProgress size={20} sx={{ color: "inherit" }} />
          ) : (
            <Icons.Google width="24" height="24" />
          )}
        </SocialButton>

        <SocialButton>
          <img
            src="/assets/kakaotalk.png"
            alt="Kakao"
            style={{
              width: "24px",
              height: "24px",
              objectFit: "contain",
            }}
          />
        </SocialButton>

        <SocialButton>
          <AppleIcon sx={{ fontSize: 24, color: "#fff" }} />
        </SocialButton>
      </Stack>

      {/* Sign-up link */}
      <Typography
        variant="body2"
        align="center"
        mt={3}
        fontFamily="Lexend"
        sx={{ color: "rgba(255,255,255,0.75)" }}
      >
        Don’t have an account?{" "}
        <Link
          href="/register"
          underline="none"
          sx={{
            color: "#E50914",
            fontWeight: 800,
            "&:hover": { color: "#ff0f1a", textDecoration: "underline" },
          }}
        >
          Sign up
        </Link>
      </Typography>
    </Paper>
  );
};

export default SignInForm;
